import { CallbackFailure, CallbackSuccess, Scalars } from '../../types';
import { Question } from './question/Question';
import { GenericFormAnswers } from './question/types';

export enum QuestionTypeEnum {
  DIVIDER = 'DIVIDER',
  SELECT = 'SELECT',
  SECTION_TITLE = 'SECTION_TITLE',
  CHECKBOX = 'CHECKBOX',
  RADIO = 'RADIO',
  TEXT = 'TEXT',
  TEXT_AREA = 'TEXT_AREA',
  TEXT_WITH_SUGGESTIONS = 'TEXT_WITH_SUGGESTIONS',
  MULTI_SELECT = 'MULTI_SELECT',
  MULTI_SELECT_PILL = 'MULTI_SELECT_PILL',
  LIKERT_SCALE = 'LIKERT_SCALE',
  CHOSEN_NAME_INPUT = 'CHOSEN_NAME_INPUT',
  CONDITIONAL_CHECKBOX_GROUP = 'CONDITIONAL_CHECKBOX_GROUP',
  MEDICATION_INPUT = 'MEDICATION_INPUT',
  MEDICATION_INPUT_WITH_SUGGESTIONS = 'MEDICATION_INPUT_WITH_SUGGESTIONS',
  PHONE_NUMBER_INPUT = 'PHONE_NUMBER_INPUT',
  EMERGENCY_CONTACT_INPUT = 'EMERGENCY_CONTACT_INPUT',
  PROFESSIONAL_RESOURCE_INPUT = 'PROFESSIONAL_RESOURCE_INPUT',
  PRIMARY_CARE_PHYSICIAN_INPUT = 'PRIMARY_CARE_PHYSICIAN_INPUT',
  COMPLEX_ERROR = 'COMPLEX_ERROR',
}
export type PendingFormProps = {
  form: GroupedFormData;
  initialValues?: GenericFormAnswers;
  questionComponent?: typeof Question;
  onChange?: (value: GenericFormAnswers | ((prev?: GenericFormAnswers) => GenericFormAnswers)) => void;
  validationErrors?: ValidationErrors;
};

export type FormSubmissionData = {
  formId: number;
  formResponseId: string;
};

export type FormSubmissionMutationData = {
  __typename?: 'Mutations';
  submitFormResponse?: {
    __typename?: 'SubmitFormResponse';
    formSubmission: {
      __typename?: 'FormResponseObject';
    } & FormSubmissionData;
  } | null;
};

export type FormSubmissionMutationVariables = {
  customerId: Scalars['String']['input'];
  formResponse: Scalars['String']['input'];
  formShortId: Scalars['String']['input'];
  isLastForm: Scalars['Boolean']['input'];
  startedAt: Scalars['DateTime']['input'];
  completedBy: Scalars['CompletedBy']['input'];
};

export enum FormSubmissionStatusCode {
  SUCCESS = 'success',
  VALIDATION_ERROR = 'validation-error',
  MISSING_REQUIRED_FIELDS = 'missing-required-fields',
  MISSING_FORM_VALUES = 'missing-form-values',
  ERROR = 'error',
}

export enum ValidationError {
  INCOMPLETE_FORM = 'incomplete-form',
}

export type ValidationErrors = Set<ValidationError>;
export type ValidationErrorsByFormTitle = Record<string, Set<ValidationError>>;

export type AnswerValue =
  | string
  | string[]
  | ContactValue[]
  | ProfessionalResourceValue
  | PrimaryCarePhysicianValue
  | MedicationValue[];

export enum FormType {
  Gad7 = 'GAD7',
  Intake = 'INTAKE',
  NoteToProvider = 'NOTE_TO_PROVIDER',
  Phq9 = 'PHQ9',
  Safetyplan = 'SAFETYPLAN',
}

export enum NoteTypeEnum {
  GROW_PROGRESS = 'GROW_PROGRESS',
}

export type NoteResponseType = Record<string, string | string[] | boolean>;

export enum ProgressNoteSectionEnum {
  APPOINTMENT_DETAIL = 'Appointment details',
  CLINICAL_PRESENTATION = 'Clinical Presentation',
  THERAPIST_INTERVENTIONS = 'Therapist Interventions',
  CLIENT_PROGRESS = 'Client Progress',
}

export type NoteType = {
  question: string;
  answer: string | string[] | boolean;
  name: string;
  margin: string;
  textVariant: string | null;
  details: string[];
  fieldType?: string | null;
};

export enum FieldTypeEnum {
  CHECKBOX = 'checkbox',
  RADIO = 'radio',
  TEXTAREA = 'textarea',
  TEXTINPUT = 'textinput',
  TEXT = 'text',
  MULTISELECT = 'multiselect',
  GOALS_INPUT = 'goals_input',
}

export type MedicationValue = { medicationName: string; dosage: string };

export interface ContactValue {
  name: string;
  phoneNumber: string;
}

export interface ProfessionalResourceValue {
  address: string;
  phoneNumber: string;
  resources: ContactValue[];
}

/**
 * I have no love for snake_casing these, but the formResponse keys here
 * have historically been this way for a long time.
 */
export interface PrimaryCarePhysicianValue {
  client_pcp_first_name: string;
  client_pcp_last_name: string;
  client_pcp_address: string;
}

export type QuestionOption = {
  value: string;
  label: string;
  followUpElements?: ProviderFormElement[];
};

export type GroupedResponse = {
  question?: string;
  questionName?: string;
  answerElement?: FormResponseElement;
  type?: QuestionTypeEnum;
  questionElement?: ProviderFormElement;
  answer?: AnswerValue;
  sectionTitle?: string;
};

export type SectionGroup = Record<string, GroupedResponse[]>;

export type GroupedFormData = {
  formShortId: ProviderForm['formShortId'];
  formPrompt: string;
  formVersion: number;
  formResponseId: string;
  formAnswers: FormResponse['formResponseModel']['pages'];
  createdAt: string;
  formTitle: ProviderForm['formModel']['formTitle'];
  formQuestionsWithAnswers: SectionGroup;
  providerId?: ProviderForm['providerId'];
} & Pick<FormResponse, 'formId' | 'formType' | 'score' | 'highAcuity'>;

export type ProviderFormElement<QuestionType = QuestionTypeEnum> = {
  name: string;
  required: boolean;
  type: QuestionType;
  title: string;
  sectionTitle?: string;
  placeholder?: string | null;
  maxLength?: number | null;
  visibleIf?: string | null;
  layout?: string | null;
  options: QuestionOption[];
};

export type ProviderForm = {
  formId: string;
  formShortId: string;
  formType: string;
  formVersion: number;
  providerId?: number;
  formModel: {
    formTitle: string;
    formPrompt: string;
    pages: {
      sectionTitle: string;
      elements: ProviderFormElement[];
    }[];
  };
};

export type FormResponseElement = {
  name: string;
  value: string;
};

export type FormResponse = {
  id: string;
  formId: string;
  formType: string;
  highAcuity: boolean;
  score: number;
  createdAt: string;
  formResponseModel: {
    pages: {
      elements: FormResponseElement[];
    }[];
  };
};

export type CombinedFormDataEntriesByFormTitle = Record<string, GroupedFormData[]>;

export type FormValuesByFormTitle = Record<string, GenericFormAnswers>;
export type FormDataGroup = {
  providerForms?: ProviderForm[];
  formResponses?: FormResponse[];
  formValuesByTitle?: FormValuesByFormTitle;
};

export type FormSubmissionRequiredFields = 'patientShortId' | 'customerId' | 'formShortId';

export type FormSubmissionCallbackResponse =
  | CallbackSuccess<FormSubmissionData | undefined, FormSubmissionStatusCode.SUCCESS>
  | CallbackFailure<ValidationErrors, FormSubmissionStatusCode.VALIDATION_ERROR>
  | CallbackFailure<null, FormSubmissionStatusCode.MISSING_FORM_VALUES>
  | CallbackFailure<Set<FormSubmissionRequiredFields>, FormSubmissionStatusCode.MISSING_REQUIRED_FIELDS>
  | CallbackFailure<Error, FormSubmissionStatusCode.ERROR>;
